import selectPropertyValueTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/selectPropertyValueTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { ColorPicker } from '@coolblue-development/next-patterns/client';
import { useIsLeasePage } from '@hooks/useIsLeasePage';
import { useTranslation } from '@i18n/useTranslation';
import { getColorAlternatives } from '@utils/collectionUtils';
import { useRouter } from 'next/navigation';

import {
  hasHandoverSessionCookie,
  renewHandoverSessionCookie
} from '../../OrderBlock/BusinessLeasing/HandoverSession';
import { CollectionType, type Discriminant, type Product, Variant } from '../Collection.types';

type CollectionColorPickerProps = {
  discriminant: Discriminant;
  index: number;
  onProductClick?: (product: Product | null) => void;
  variant: Variant;
  selectedProductId?: number;
  collectionType: CollectionType;
  colorLabel?: string;
};

export const CollectionColorPicker = ({
  discriminant,
  index,
  onProductClick,
  variant,
  selectedProductId,
  collectionType,
  colorLabel
}: CollectionColorPickerProps) => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation('webshop.productPage');
  const isDesktop = variant === Variant.DESKTOP;
  const isMobile = variant === Variant.MOBILE;

  const popoverStrings = {
    temporaryOutOfStock: isDesktop ? t('temporaryOutOfStock') : '',
    notAvailableInVariant: isDesktop ? t('notAvailableInVariant') : ''
  };

  discriminant.values = discriminant.values
    .filter(value => value.shouldShowValue)
    .sort((a, b) => (b.isValueOfCurrentProduct ? 1 : 0) - (a.isValueOfCurrentProduct ? 1 : 0));

  const colorIndex = discriminant.values.findIndex(
    value => value.product?.id === selectedProductId
  );

  const isLeasePage = useIsLeasePage();

  const onClickColor = (index: string) => {
    // Get the values of the current discriminant then use the index to find the one that was clicked
    const clickedProduct = discriminant.values[Number(index)].product;
    if (clickedProduct) {
      trackEvent(
        selectPropertyValueTrigger({
          sub_feature:
            collectionType === CollectionType.DROPDOWN
              ? 'drop_down_selector'
              : 'multi_discriminant_selector',
          index: Number(index) + 1,
          total_indices: discriminant.values.length,
          property_name: discriminant.displayName,
          property_value: discriminant.values[Number(index)].name,
          item_id: clickedProduct.id
        })
      );
      let redirectUrl = clickedProduct.urls.product;

      if (isLeasePage) {
        redirectUrl += '?lease=1';
        if (hasHandoverSessionCookie()) {
          renewHandoverSessionCookie();
          redirectUrl += '&bho=1';
        }
      }

      if (redirectUrl && isDesktop) {
        router.push(redirectUrl);
      }
      if (onProductClick && isMobile) {
        onProductClick(clickedProduct);
      }
    }
  };

  return (
    <ColorPicker
      key={`color-${index}`}
      colorAlternatives={getColorAlternatives(discriminant.values, popoverStrings)}
      size='medium'
      onChange={onClickColor}
      colorPickerDisplay='wrap'
      value={colorIndex?.toString()}
      colorLabel={colorLabel}
    />
  );
};
